import { useCallback, useEffect, useState } from 'react';

/**
 * 라이트/다크 테마 상태 훅.
 *
 * <html>의 `data-brand` 속성을 토글해 index.css의 `[data-brand="dark"]`
 * 규칙을 활성/비활성화한다. 사용자 선택은 localStorage에 보존.
 *
 * - 기본값: 사용자가 한 번도 선택하지 않았으면 'dark'(기존 동작 유지)
 */
export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'lb-theme';

function readInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'dark';
  const saved = window.localStorage.getItem(STORAGE_KEY);
  if (saved === 'light' || saved === 'dark') return saved;
  return 'dark';
}

function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (theme === 'dark') {
    root.setAttribute('data-brand', 'dark');
    root.classList.add('dark');
  } else {
    root.removeAttribute('data-brand');
    root.classList.remove('dark');
  }
}

// 초기 렌더 직전에 한 번 실행해 깜빡임 방지
if (typeof document !== 'undefined') {
  applyTheme(readInitialTheme());
}

export function useTheme(): { theme: Theme; setTheme: (t: Theme) => void; toggleTheme: () => void } {
  const [theme, setThemeState] = useState<Theme>(() => readInitialTheme());

  useEffect(() => {
    applyTheme(theme);
    window.localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  const setTheme = useCallback((next: Theme) => setThemeState(next), []);
  const toggleTheme = useCallback(() => setThemeState((t) => (t === 'dark' ? 'light' : 'dark')), []);

  return { theme, setTheme, toggleTheme };
}
